import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Plus, Minus, Trash2, Award } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, imgUrl } from "@/lib/api";

export default function CartDrawer() {
    const { t } = useTranslation();
    const { items, open, setOpen, updateQty, removeItem, subtotal, grossSubtotal, bundleSavings, bundlePctFor } = useCart();
    const { user } = useAuth();
    const [loyalty, setLoyalty] = useState(null);

    useEffect(() => {
        if (open && user && user !== false) {
            api.get("/loyalty/balance").then((r) => setLoyalty(r.data)).catch(() => {});
        }
    }, [open, user]);

    return (
        <>
            <div
                className={`fixed inset-0 z-[60] bg-white/70 backdrop-blur-sm transition-opacity ${
                    open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                }`}
                onClick={() => setOpen(false)}
                data-testid="cart-overlay"
            />
            <aside
                className={`fixed right-0 top-0 bottom-0 z-[70] w-full max-w-md bg-white border-l border-neutral-200 transform transition-transform duration-300 ${
                    open ? "translate-x-0" : "translate-x-full"
                }`}
                data-testid="cart-drawer"
            >
                <div className="flex items-center justify-between px-5 sm:px-6 h-16 sm:h-20 border-b border-neutral-200">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-[#0077b6]">
                            {t("cart.eyebrow")}
                        </div>
                        <div className="font-display font-bold text-lg">
                            {items.length === 1
                                ? t("cart.countOne")
                                : t("cart.countOther", { count: items.length })}
                        </div>
                    </div>
                    <button
                        onClick={() => setOpen(false)}
                        className="p-2 hover:text-black"
                        data-testid="cart-close-btn"
                        aria-label={t("common.close")}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex flex-col h-[calc(100%-4rem)] sm:h-[calc(100%-5rem)]">
                    {items.length > 0 && loyalty?.next_tier && (
                        <div
                            className="mx-5 sm:mx-6 mt-4 flex items-center gap-3 rounded-lg border border-[#00aeef]/30 bg-[#00aeef]/[0.05] px-3 py-2.5"
                            data-testid="cart-tier-countdown"
                        >
                            <Award className="w-4 h-4 text-[#0077b6] flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-neutral-700 font-medium">
                                    {t("cart.tierCountdown", {
                                        points: loyalty.next_tier.points_to_next.toLocaleString(),
                                        tier: loyalty.next_tier.label,
                                    })}
                                </div>
                                <div className="w-full h-1.5 bg-neutral-100 rounded-full overflow-hidden mt-1.5">
                                    <div
                                        className="h-full bg-[#0089c8] transition-all duration-500"
                                        style={{
                                            width: `${Math.min(
                                                100,
                                                Math.round((loyalty.lifetime_points / loyalty.next_tier.min_lifetime) * 100)
                                            )}%`,
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {items.length > 0 && loyalty && !loyalty.next_tier && loyalty.tier && (
                        <div
                            className="mx-5 sm:mx-6 mt-4 flex items-center gap-3 rounded-lg border border-[#00aeef]/30 bg-[#00aeef]/[0.05] px-3 py-2.5"
                            data-testid="cart-tier-top"
                        >
                            <Award className="w-4 h-4 text-[#0077b6] flex-shrink-0" />
                            <div className="text-xs text-neutral-700 font-medium">
                                {t("cart.tierTopReached", { tier: loyalty.tier.label })}
                            </div>
                        </div>
                    )}
                    <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 space-y-4">
                        {items.length === 0 && (
                            <div className="text-center py-20 text-neutral-500" data-testid="cart-empty">
                                <div className="text-xs uppercase tracking-[0.22em] mb-2">{t("cart.emptyTitle")}</div>
                                <div className="text-sm">{t("cart.emptyBody")}</div>
                            </div>
                        )}
                        {items.map((item) => (
                            <div
                                key={item.variant_id || item.product_id}
                                className="flex gap-4 p-3 border border-neutral-200 hover:border-[#0089c8]/30 transition-colors rounded-lg"
                                data-testid={`cart-item-${item.slug}${item.dose ? `-${item.dose}` : ""}`}
                            >
                                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white border border-neutral-200 overflow-hidden flex-shrink-0 rounded-lg">
                                    <img
                                        src={imgUrl(item.image)}
                                        alt={item.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <Link
                                        to={`/product/${item.slug}`}
                                        onClick={() => setOpen(false)}
                                        className="font-display font-semibold text-sm hover:text-black"
                                    >
                                        {item.name}
                                    </Link>
                                    <div className="text-xs text-neutral-500 mt-1 flex items-center gap-2 flex-wrap">
                                        {item.dose && (
                                            <span className="font-mono text-[#0077b6] border border-[#00aeef]/30 px-1.5 py-0.5 rounded text-[10px]">
                                                {item.dose}
                                            </span>
                                        )}
                                        <span>£{item.price.toFixed(2)} {t("cart.each")}</span>
                                        {bundlePctFor(item.quantity) > 0 && (
                                            <span
                                                className="text-[10px] uppercase tracking-[0.18em] font-bold text-white bg-[#0089c8] px-1.5 py-0.5 rounded"
                                                data-testid={`bundle-badge-${item.slug}`}
                                            >
                                                {t("cart.bundleApplied", {
                                                    pct: Math.round(bundlePctFor(item.quantity) * 100),
                                                })}
                                            </span>
                                        )}
                                    </div>
                                    {(item.quantity === 2 || item.quantity === 4) && (
                                        <button
                                            type="button"
                                            onClick={() => updateQty(item.slug, item.quantity + 1, item.dose)}
                                            className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#0077b6] hover:text-[#005f92] border border-dashed border-[#00aeef]/50 hover:border-[#00aeef] bg-[#00aeef]/[0.05] hover:bg-[#00aeef]/[0.12] px-2 py-1 rounded transition-colors"
                                            data-testid={`bundle-nudge-${item.slug}`}
                                        >
                                            {t("cart.bundleNudge", {
                                                pct: item.quantity === 2 ? 5 : 10,
                                            })}
                                        </button>
                                    )}
                                    <div className="mt-2 flex items-center justify-between">
                                        <div className="flex items-center border border-neutral-200 rounded-lg">
                                            <button
                                                onClick={() => updateQty(item.variant_id, item.quantity - 1)}
                                                className="p-1.5 hover:text-black"
                                                data-testid={`qty-dec-${item.slug}`}
                                            >
                                                <Minus className="w-3 h-3" />
                                            </button>
                                            <span className="px-3 text-sm font-mono">{item.quantity}</span>
                                            <button
                                                onClick={() => updateQty(item.variant_id, item.quantity + 1)}
                                                className="p-1.5 hover:text-black"
                                                data-testid={`qty-inc-${item.slug}`}
                                            >
                                                <Plus className="w-3 h-3" />
                                            </button>
                                        </div>
                                        <button
                                            onClick={() => removeItem(item.variant_id)}
                                            className="text-neutral-500 hover:text-red-400 p-1"
                                            data-testid={`remove-${item.slug}`}
                                            aria-label={t("cart.remove")}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {items.length > 0 && (
                        <div className="border-t border-neutral-200 p-5 sm:p-6 space-y-4">
                            {bundleSavings > 0 && (
                                <div
                                    className="flex items-center justify-between text-xs"
                                    data-testid="cart-bundle-savings"
                                >
                                    <div className="uppercase tracking-[0.22em] text-[#0077b6] font-semibold">
                                        {t("cart.bundleSavings")}
                                    </div>
                                    <div className="font-display font-bold text-[#0077b6]">
                                        −£{bundleSavings.toFixed(2)}
                                    </div>
                                </div>
                            )}
                            <div className="flex items-center justify-between">
                                <div className="text-xs uppercase tracking-[0.22em] text-neutral-600">
                                    {t("cart.subtotal")}
                                </div>
                                <div className="text-right">
                                    {bundleSavings > 0 && (
                                        <div className="text-xs text-neutral-400 line-through">
                                            £{grossSubtotal.toFixed(2)}
                                        </div>
                                    )}
                                    <div className="font-display text-2xl font-bold" data-testid="cart-subtotal">
                                        £{subtotal.toFixed(2)}
                                    </div>
                                </div>
                            </div>
                            <div className="text-xs text-neutral-500">
                                {subtotal >= 150
                                    ? t("cart.freeShipping")
                                    : t("cart.towardFreeShipping", { amount: (150 - subtotal).toFixed(2) })}
                            </div>
                            <Link
                                to="/checkout"
                                onClick={() => setOpen(false)}
                                className="block w-full text-center bg-[#0089c8] hover:bg-[#0077b6] rounded-lg text-white font-bold uppercase tracking-[0.18em] text-xs py-4 transition-colors"
                                data-testid="cart-checkout-btn"
                            >
                                {t("cart.checkout")}
                            </Link>
                            <Link
                                to="/bulk-quote?fromCart=1"
                                onClick={() => setOpen(false)}
                                className="block w-full text-center border border-[#00aeef]/40 bg-[#00aeef]/[0.06] hover:bg-[#00aeef]/[0.12] text-[#0077b6] rounded-lg font-bold uppercase tracking-[0.18em] text-[11px] py-3 transition-colors"
                                data-testid="cart-request-bulk-quote"
                            >
                                {t("cart.requestBulkQuote")}
                            </Link>
                            <Link
                                to="/shop"
                                onClick={() => setOpen(false)}
                                className="block w-full text-center text-neutral-600 hover:text-neutral-900 text-xs uppercase tracking-[0.18em]"
                                data-testid="cart-continue-shopping"
                            >
                                {t("cart.keepBrowsing")}
                            </Link>
                        </div>
                    )}
                </div>
            </aside>
        </>
    );
}
